import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Loader2, Plus, Search, Car, ShieldCheck, Upload, Trash2 } from 'lucide-react'
import { useAdminCarBrands, useCreateBrand, useDeleteBrand } from '../../../hooks/use-admin-cars'
import { DeleteConfirmDialog } from '../components/delete-confirm-dialog'
import { http } from '@/lib/api-client'

export function BrandsPage() {
  const { data: brands, isLoading } = useAdminCarBrands()
  const createBrand = useCreateBrand()
  const deleteBrand = useDeleteBrand()

  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<any>(null)
  const [name, setName] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [isFeatured, setIsFeatured] = useState(false)
  const [displayOrder, setDisplayOrder] = useState('0')

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingLogo(true)
    try {
      const formData = new FormData()
      formData.append('logo', file)
      const res = await http.post<{ success: boolean; data: { url: string } }>('/cars/upload/brand-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      if (res.data?.data?.url) {
        setLogoUrl(res.data.data.url)
      }
    } catch (err: any) {
      alert(err.response?.data?.message || err.message || 'Logo upload failed')
    } finally {
      setUploadingLogo(false)
      e.target.value = ''
    }
  }

  const filteredBrands = (brands || []).filter((b: any) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  )

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    await createBrand.mutateAsync({
      name,
      logoUrl: logoUrl || undefined,
      isFeatured,
      displayOrder: Number(displayOrder) || 0,
    })
    setName('')
    setLogoUrl('')
    setIsFeatured(false)
    setDisplayOrder('0')
    setOpen(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Car Brands Catalog</h1>
          <p className="text-sm text-muted-foreground">Manage canonical manufacturer brands, logos, and display priority.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger>
            <Button className="gap-2">
              <Plus className="size-4" /> Add New Brand
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Car Manufacturer Brand</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Brand Name *</label>
                <Input placeholder="e.g. Mercedes-Benz" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Brand Logo Image</label>
                <div className="flex gap-2 items-center">
                  <Input placeholder="Upload logo file or paste URL (https://...)" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} />
                  <label className="inline-flex cursor-pointer shrink-0">
                    <span className="inline-flex items-center gap-1.5 px-3 py-2 bg-secondary text-secondary-foreground font-semibold text-xs rounded-lg hover:bg-secondary/80">
                      {uploadingLogo ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
                      Upload
                    </span>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} className="hidden" />
                  </label>
                </div>
                {logoUrl && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={logoUrl} alt="Logo preview" className="size-8 object-contain rounded border border-border" />
                    <span className="text-[11px] text-muted-foreground truncate max-w-xs">{logoUrl}</span>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Display Order</label>
                <Input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <input type="checkbox" id="featured" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} className="rounded border-border" />
                <label htmlFor="featured" className="text-sm font-semibold">Featured Brand on Home/Marketplace</label>
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createBrand.isPending || uploadingLogo}>
                  {createBrand.isPending ? <Loader2 className="size-4 animate-spin" /> : 'Save Brand'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
          <Input placeholder="Search brands..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Brands Table / Grid */}
      {isLoading ? (
        <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading manufacturer brands...</p>
        </div>
      ) : (
        <Card className="overflow-hidden border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-accent/40 text-muted-foreground border-b border-border uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-4">Brand</th>
                <th className="p-4">Slug</th>
                <th className="p-4">Models</th>
                <th className="p-4">Listings</th>
                <th className="p-4">Featured</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredBrands.map((b: any) => {
                const isIndependent = (b._count?.models ?? 0) === 0 && (b._count?.listings ?? 0) === 0
                return (
                  <tr key={b.id} className="hover:bg-accent/20 transition-colors">
                    <td className="p-4 font-bold flex items-center gap-3">
                      {b.logoUrl ? (
                        <img src={b.logoUrl} alt={b.name} className="size-8 object-contain rounded-md border border-border" />
                      ) : (
                        <div className="size-8 rounded-md border border-border bg-accent/30 flex items-center justify-center">
                          <Car className="size-4 text-primary" />
                        </div>
                      )}
                      <span>{b.name}</span>
                    </td>
                    <td className="p-4 font-mono text-xs text-muted-foreground">{b.slug}</td>
                    <td className="p-4 font-semibold">{b._count?.models ?? 0}</td>
                    <td className="p-4 font-semibold">{b._count?.listings ?? 0}</td>
                    <td className="p-4">
                      {b.isFeatured ? (
                        <Badge className="bg-amber-500/20 text-amber-500 border border-amber-500/30">
                          <ShieldCheck className="size-3 mr-1" /> Featured
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Standard</span>
                      )}
                    </td>
                    <td className="p-4">
                      <Badge className={b.isActive ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted text-muted-foreground'}>
                        {b.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget(b)}
                        className={`size-8 p-0 ${
                          isIndependent
                            ? 'text-rose-500 hover:text-rose-600 hover:bg-rose-500/10'
                            : 'text-muted-foreground/50 hover:text-amber-600 hover:bg-amber-500/10'
                        }`}
                        title={
                          isIndependent
                            ? `Delete independent brand "${b.name}"`
                            : `Cannot delete: Has ${b._count?.models || 0} models and ${b._count?.listings || 0} listings`
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteBrand.mutateAsync(deleteTarget.id)
          }}
          title={`Delete Brand: ${deleteTarget.name}`}
          entityName={deleteTarget.name}
          entityType="Brand"
          isIndependent={(deleteTarget._count?.models ?? 0) === 0 && (deleteTarget._count?.listings ?? 0) === 0}
          blockingReason={`Cannot delete "${deleteTarget.name}" because it has ${deleteTarget._count?.models || 0} model(s) and ${deleteTarget._count?.listings || 0} listing(s) attached. Only independent brands can be deleted.`}
          dependencyDetails={[
            { label: 'Models', count: deleteTarget._count?.models ?? 0 },
            { label: 'Listings', count: deleteTarget._count?.listings ?? 0 },
          ]}
        />
      )}
    </div>
  )
}
